// Close Intelligence — CI_03 "Forecast context" pure helpers.
//
// Kept DOM-free so the page and the Jest suite share one implementation. The
// unit forecast baseline is read-only: the "expectation" for a close period IS
// the latest governed forecast value (shaped server-side via the bridge seam).
// These helpers only build request params, normalize the payload, and format
// cells — no calculation forks (numbers are already tied to the bridge/board).

export const FORECAST_BASES = ['prior', 'budget'];
export const DEFAULT_BASIS = 'prior';
export const DEFAULT_LIMIT = 100;

// Local previous-month key (YYYY-MM), matching the Monitor / Close-run pattern
// so CI_03 opens on the same default close period the rest of the module uses.
export const previousMonthKey = (date = new Date()) => {
  const base = date instanceof Date && !Number.isNaN(date.getTime()) ? date : new Date();
  const d = new Date(base.getFullYear(), base.getMonth() - 1, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const isBlank = (value) =>
  value === undefined || value === null || (typeof value === 'string' && value.trim() === '');

// Build the /forecast-context/units query params from filter state, omitting
// blanks. Numbers (including offset 0) are preserved; empty strings dropped.
export const buildForecastContextParams = (filters = {}) => {
  const {
    period,
    basis,
    market,
    lob,
    entity,
    metricKey,
    limit,
    offset,
  } = filters;

  const params = {};
  if (!isBlank(period)) params.period = String(period).slice(0, 7);
  params.basis = FORECAST_BASES.includes(basis) ? basis : DEFAULT_BASIS;
  if (!isBlank(market)) params.market = market;
  if (!isBlank(lob)) params.lob = lob;
  if (!isBlank(entity)) params.entity = String(entity).trim();
  if (!isBlank(metricKey)) params.metricKey = metricKey;

  const limitNum = Number(limit);
  if (Number.isFinite(limitNum) && limitNum > 0) params.limit = limitNum;
  const offsetNum = Number(offset);
  if (Number.isFinite(offsetNum) && offsetNum >= 0) params.offset = offsetNum;

  return params;
};

// Currency formatter for forecast/actual/variance cells. null/undefined -> em
// dash; zero renders as $0; magnitudes are whole dollars (values are already in
// dollars, often millions).
export const formatForecastAmount = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  const num = Number(value);
  if (!Number.isFinite(num)) return '—';
  const sign = num < 0 ? '-' : '';
  return `${sign}$${Math.abs(Math.round(num)).toLocaleString('en-US')}`;
};

// Signed variance percentage. Backend variancePct is a fraction (0.0125 = 1.25%).
export const formatVariancePct = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  const num = Number(value);
  if (!Number.isFinite(num)) return '—';
  const pct = num * 100;
  const sign = pct > 0 ? '+' : '';
  return `${sign}${pct.toFixed(2)}%`;
};

// A row is a plain forecast baseline unless an active override supersedes it.
export const forecastOverrideStatus = (row) =>
  row && row.override ? 'Override' : 'Baseline';

// ---------------------------------------------------------------- override editor

// An override value must be a finite number (0 is valid; blank / non-numeric is not).
export const isOverrideValueValid = (value) => {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string' && value.trim() === '') return false;
  return Number.isFinite(Number(value));
};

// A non-blank note is required for every audited override.
export const isOverrideNoteValid = (note) =>
  typeof note === 'string' && note.trim().length > 0;

// A source version must be present so the override binds to a concrete baseline.
const isSourceVersionValid = (version) =>
  typeof version === 'string' && version.trim().length > 0;

// Save is allowed only when value + note + source version are all valid. The page
// additionally disables the button while a save is in flight.
export const canSaveOverride = ({ overrideValue, note, sourceVersion } = {}) =>
  isOverrideValueValid(overrideValue) &&
  isOverrideNoteValid(note) &&
  isSourceVersionValid(sourceVersion);

// Build the POST /forecast-context/overrides payload from a baseline row plus the
// editor draft. sourceValue is carried verbatim so the audit trail preserves the
// forecast baseline the user departed from; basis comes from the table state (rows
// don't carry it). Actor is attached by closeIntelApi.saveForecastOverride.
export const buildForecastOverridePayload = (row = {}, draft = {}) => {
  const { overrideValue, note, basis } = draft;
  return {
    planningEntity: row.planningEntity,
    metricKey: row.metricKey,
    metricLabel: row.metricLabel || row.metricKey,
    period: row.period,
    basis,
    sourceVersion: row.sourceVersion,
    sourceValue: row.sourceValue,
    overrideValue: Number(overrideValue),
    note: typeof note === 'string' ? note.trim() : note,
    market: row.market,
    lob: row.lob,
  };
};

// Normalize the endpoint payload into a stable shape the table can render even
// when the response is empty / partial (offline, no DB, degraded).
export const normalizeForecastRows = (payload) => {
  const p = payload || {};
  const pagination = p.pagination || {};
  return {
    period: p.period || '',
    basis: p.basis || DEFAULT_BASIS,
    forecastVersion: p.forecastVersion || '',
    versionProvenance: p.versionProvenance || null,
    rows: Array.isArray(p.rows) ? p.rows : [],
    pagination: {
      limit: Number.isFinite(Number(pagination.limit)) ? Number(pagination.limit) : DEFAULT_LIMIT,
      offset: Number.isFinite(Number(pagination.offset)) ? Number(pagination.offset) : 0,
      total: Number.isFinite(Number(pagination.total)) ? Number(pagination.total) : 0,
    },
  };
};

// Distinct market / LOB option lists harvested from the current page of rows,
// for the client-side filter selects.
export const collectForecastScopeOptions = (rows = []) => {
  const markets = [...new Set(rows.map((row) => row.market).filter(Boolean))].sort();
  const lobs = [...new Set(rows.map((row) => row.lob).filter(Boolean))].sort();
  const metrics = [
    ...new Map(
      rows
        .filter((row) => row.metricKey)
        .map((row) => [row.metricKey, row.metricLabel || row.metricKey]),
    ).entries(),
  ].sort((a, b) => String(a[1]).localeCompare(String(b[1])));
  return { markets, lobs, metrics };
};
